import { useCallback } from 'react';
import { useDuplicateTasksMutation } from 'shared/hooks/queries/use-duplicate-tasks-mutation';
import { useRelationships } from 'shared/hooks/use-relationships';
import { getTaskDocs } from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { chunkArray } from 'shared/utils/chunk-array';

import { updateLinkedGoal } from './update-linked-goal';
import { updateLinkedLifeArea } from './update-linked-life-area';
import { updateLinkedRelationships } from './update-linked-relationships';

export const useDuplicateTasks = () => {
  const { mutateAsync: duplicateTasks } = useDuplicateTasksMutation();
  const relationships = useRelationships();

  return useCallback(
    async (tasks: Task[]) => {
      if (!tasks.length) {
        return;
      }

      // Collect all subtasks from all tasks
      const allSubtaskIds = new Set<ID>();
      tasks.forEach((task) => {
        if (task.childIds?.length) {
          task.childIds.forEach((childId) => allSubtaskIds.add(childId));
        }
      });

      // Fetch all subtasks
      const subtasks =
        allSubtaskIds.size > 0
          ? (
              await Promise.all(
                chunkArray(Array.from(allSubtaskIds), 30).map(
                  async (chunkedSubTaskIds) =>
                    await getTaskDocs({ ids: chunkedSubTaskIds }),
                ),
              )
            ).flat(1)
          : [];

      // Duplicate all tasks (main tasks + subtasks)
      const newTasks = await duplicateTasks([...tasks, ...subtasks]);

      // Create map from old task ID to new task
      const oldToNewTaskMap = new Map<ID, Task>();
      [...tasks, ...subtasks].forEach((oldTask, index) => {
        const newTask = newTasks[index];
        if (newTask) {
          oldToNewTaskMap.set(oldTask.id, newTask);
        }
      });

      // Filter to only main tasks (no parentIds) for entity updates
      const mainTaskPairs = tasks
        .filter((task) => !task.parentIds?.length)
        .map((oldTask) => {
          const newTask = oldToNewTaskMap.get(oldTask.id);
          if (!newTask) {
            return null;
          }
          return { oldTask, newTask };
        })
        .filter(
          (pair): pair is { oldTask: Task; newTask: Task } => pair !== null,
        );

      if (!mainTaskPairs.length) {
        return;
      }

      // Update linked entities (each entity updated only once)
      await Promise.all([
        updateLinkedGoal(mainTaskPairs),
        updateLinkedLifeArea(mainTaskPairs),
        updateLinkedRelationships(mainTaskPairs, relationships),
      ]);
    },
    [duplicateTasks, relationships],
  );
};
